export interface TTSResponseData {
  audioBlob: Blob;
  sampleRate: number;
  durationSeconds: number;
  synthesisTimeMs: number;
  realTimeFactor: number;
}

export interface ASRResponseData {
  success: boolean;
  text: string;
  language?: string;
  duration?: number;
  model: string;
  latencyMs: number;
  error?: string;
}

export interface HealthResponse {
  status: string;
  engine: string;
  target_hardware: string;
  tts: {
    voice: string;
    runtime: string;
  };
  asr: {
    model: string;
  };
}

const STORAGE_KEY_OR = 'hmd_tts_openrouter_key';
const STORAGE_KEY_BASE_URL = 'hmd_tts_api_base_url';
export function getStoredOpenRouterKey(): string {
  return localStorage.getItem(STORAGE_KEY_OR) || '';
}

export function setStoredOpenRouterKey(key: string): void {
  if (key) {
    localStorage.setItem(STORAGE_KEY_OR, key.trim());
  } else {
    localStorage.removeItem(STORAGE_KEY_OR);
  }
}

export function getStoredBaseUrl(): string {
  return localStorage.getItem(STORAGE_KEY_BASE_URL) || '';
}

export function setStoredBaseUrl(url: string): void {
  if (url) {
    localStorage.setItem(STORAGE_KEY_BASE_URL, url.trim().replace(/\/$/, ''));
  } else {
    localStorage.removeItem(STORAGE_KEY_BASE_URL);
  }
}

function resolveApiUrl(path: string): string {
  const base = getStoredBaseUrl();
  if (base) return `${base}${path}`;

  // If running on Cloudflare Pages (*.pages.dev), target the deployed Worker API directly
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('.pages.dev')) {
    return `https://hmd-tts-ars-api.feiko-lai.workers.dev${path}`;
  }

  return path;
}

export async function requestTTS(
  text: string,
  options: { speed?: number; pitch?: number; volume?: number } = {}
): Promise<TTSResponseData> {
  const url = resolveApiUrl('/api/tts');
  const t0 = performance.now();

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text,
      speed: options.speed,
      pitch: options.pitch,
      volume: options.volume,
      format: 'wav',
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`TTS synthesis error (${response.status}): ${errorText}`);
  }

  const sampleRate = Number(response.headers.get('X-Sample-Rate')) || 48000;
  const durationSeconds = Number(response.headers.get('X-Duration-Seconds')) || 0;
  const synthesisTimeMs = Number(response.headers.get('X-Synthesis-Time-Ms')) || Math.round(performance.now() - t0);
  const realTimeFactor = Number(response.headers.get('X-Real-Time-Factor')) || 0;

  const audioBlob = await response.blob();

  return {
    audioBlob,
    sampleRate,
    durationSeconds,
    synthesisTimeMs,
    realTimeFactor,
  };
}

export async function requestASR(
  audioBlob: Blob,
  language?: string
): Promise<ASRResponseData> {
  const url = resolveApiUrl('/api/asr');
  const apiKey = getStoredOpenRouterKey();

  const formData = new FormData();
  formData.append('file', audioBlob, 'speech.wav');
  formData.append('format', 'wav');
  if (language && language !== 'auto') {
    formData.append('language', language);
  }

  const headers: Record<string, string> = {};
  if (apiKey) {
    headers['X-OpenRouter-Key'] = apiKey;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = (await response.json()) as ASRResponseData;
  if (!response.ok) {
    throw new Error(data.error || `ASR failed with status ${response.status}`);
  }

  return data;
}

export async function requestHealth(): Promise<HealthResponse> {
  const url = resolveApiUrl('/api/health');
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Health check failed: ${response.status}`);
  }
  return (await response.json()) as HealthResponse;
}
